// Shared reverse-geocoding helpers. Single source of truth for the app's geocode
// strategy: Google Geocoder (when the `google_reverse_geocoding` feature is on + a
// Google Maps key is present + the Maps JS has loaded) else self-hosted Nominatim.

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// The inline maps lazy-load the Google Maps JS, so `window.google.maps.Geocoder`
// may not exist on the first interaction. Poll briefly for it before falling back.
export const waitForGeocoder = (timeoutMs = 8000) =>
  new Promise((resolve) => {
    if (window.google?.maps?.Geocoder) return resolve(true);
    const start = Date.now();
    const id = setInterval(() => {
      if (window.google?.maps?.Geocoder) {
        clearInterval(id);
        resolve(true);
      } else if (Date.now() - start > timeoutMs) {
        clearInterval(id);
        resolve(false);
      }
    }, 200);
  });

// Build a clean, compact address from a Nominatim reverse-geocode response.
export const formatNominatimAddress = (data) => {
  const a = data?.address;
  let clean = data?.display_name || 'Address not found';
  if (a) {
    const parts = [];
    const street = [a.house_number, a.road].filter(Boolean).join(' ');
    if (street) parts.push(street);
    const suburb = a.suburb || a.village || a.town || a.hamlet;
    if (suburb) parts.push(suburb);
    if (a.state) parts.push(a.state);
    if (a.postcode) parts.push(a.postcode);
    if (a.country) parts.push(a.country);
    if (parts.length) clean = parts.join(', ');
  }
  return clean;
};

// Reverse-geocode a single point. `useGoogle` = caller already decided Google is the
// intended provider (flag on + key present). Uses Google only if its Geocoder is loaded,
// otherwise falls back to Nominatim, then to a "not configured" message.
export const reverseGeocode = (lat, lng, { useGoogle, nominatimBaseUrl } = {}) => {
  const latitude = Number(lat);
  const longitude = Number(lng);
  if (useGoogle && window.google?.maps?.Geocoder) {
    return new Promise((resolve) => {
      new window.google.maps.Geocoder().geocode(
        { location: { lat: latitude, lng: longitude } },
        (results, status) =>
          resolve(status === 'OK' && results[0] ? results[0].formatted_address : 'Address not found'),
      );
    });
  }
  if (nominatimBaseUrl) {
    return fetch(`${nominatimBaseUrl}/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`)
      .then((r) => r.json())
      .then(formatNominatimAddress)
      .catch(() => 'Geocoding failed');
  }
  return Promise.resolve('Geocoding not configured');
};

// Reverse-geocode many points (aligned output array) for bulk operations like CSV export.
// Waits once for the Google Geocoder when intended + a Maps script is already loading,
// then resolves with limited concurrency to stay friendly to the geocoder.
export const reverseGeocodeBatch = async (points, { useGoogle, nominatimBaseUrl, concurrency = 5 } = {}) => {
  let googleReady = useGoogle && !!window.google?.maps?.Geocoder;
  if (useGoogle && !googleReady) {
    const mapsLoading = !!document.querySelector('script[src*="maps.googleapis.com"]') || !!window.google;
    if (mapsLoading) googleReady = await waitForGeocoder(6000);
  }
  const effUseGoogle = useGoogle && googleReady;
  const results = new Array(points.length).fill('');
  let cursor = 0;
  const worker = async () => {
    while (cursor < points.length) {
      const i = cursor++;
      const p = points[i];
      if (p && p.lat != null && p.lng != null) {
        results[i] = await reverseGeocode(p.lat, p.lng, { useGoogle: effUseGoogle, nominatimBaseUrl });
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, points.length || 1) }, worker));
  return results;
};

export { GOOGLE_MAPS_API_KEY };
